/**
 * WebSocket Hook for Real-time Updates
 * Replaces polling with WebSocket connections for live events.
 */
import { useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ==================== CONFIGURATION ====================

// Feature flag for WebSockets - can be disabled if infrastructure doesn't support it
const WS_ENABLED = process.env.REACT_APP_WEBSOCKET_ENABLED !== 'false';

const WS_CONFIG = {
  // Reconnection
  minReconnectDelay: 1000,
  maxReconnectDelay: 30000,
  reconnectDecay: 1.5,
  jitterFactor: 0.3,
  maxReconnectAttempts: 10, // Reduced - if it doesn't work after 10 attempts, stop trying
  fastReconnectWindow: 5000,
  
  // Heartbeat
  pingInterval: 20000,
  pongTimeout: 10000,
  missedPongsAllowed: 2,
  
  // Storage keys
  lastSequenceKey: 'ws_last_sequence',
  subscriptionsKey: 'ws_subscriptions',
  
  // Connection timeout - if no stable connection after this, disable WS
  connectionStabilityTimeout: 30000,
};


// ==================== EVENT DEDUPLICATOR ====================

class EventDeduplicator {
  constructor(maxSize = 1000) {
    this.seen = new Set();
    this.order = [];
    this.maxSize = maxSize;
  }

  isDuplicate(eventId) {
    if (this.seen.has(eventId)) return true;
    
    if (this.seen.size >= this.maxSize) {
      const oldest = this.order.shift();
      this.seen.delete(oldest);
    }
    
    this.seen.add(eventId);
    this.order.push(eventId);
    return false;
  }

  clear() {
    this.seen.clear();
    this.order = [];
  }
}


// ==================== RECONNECT MANAGER ====================

class ReconnectManager {
  constructor(config) {
    this.config = config;
    this.attempts = 0;
    this.lastConnectedAt = null;
    this.timer = null;
  }

  onConnected() {
    this.attempts = 0;
    this.lastConnectedAt = Date.now();
  }

  scheduleReconnect(connectFn) {
    this.attempts++;
    
    if (this.attempts > this.config.maxReconnectAttempts) {
      return false;
    }
    
    // Fast reconnect if recently connected
    const timeSinceConnected = Date.now() - (this.lastConnectedAt || 0);
    let delay;
    
    if (timeSinceConnected < this.config.fastReconnectWindow) {
      delay = this.config.minReconnectDelay;
    } else {
      // Exponential backoff with jitter
      delay = Math.min(
        this.config.minReconnectDelay * Math.pow(this.config.reconnectDecay, this.attempts),
        this.config.maxReconnectDelay
      );
      
      const jitter = delay * this.config.jitterFactor * (Math.random() * 2 - 1);
      delay = Math.round(delay + jitter);
    }
    
    
    this.timer = setTimeout(() => {
      connectFn();
    }, delay);
    
    return true;
  }

  cancel() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  reset() {
    this.cancel();
    this.attempts = 0;
  }
}


// ==================== HEARTBEAT MANAGER ====================

class HeartbeatManager {
  constructor(config, sendFn, onTimeout) {
    this.config = config;
    this.sendFn = sendFn;
    this.onTimeout = onTimeout;
    
    this.pingTimer = null;
    this.pongTimer = null;
    this.missedPongs = 0;
  }

  start() {
    this.stop();
    this.pingTimer = setInterval(() => this.sendPing(), this.config.pingInterval);
  }

  sendPing() {
    this.sendFn({
      type: 'ping',
      timestamp: Date.now()
    });
    
    // Start pong timeout
    this.pongTimer = setTimeout(() => {
      this.missedPongs++;
      
      if (this.missedPongs > this.config.missedPongsAllowed) {
        this.onTimeout();
      }
    }, this.config.pongTimeout);
  }

  handlePong() {
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
    this.missedPongs = 0;
  }

  stop() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
    this.missedPongs = 0;
  }
}


// ==================== MAIN HOOK ====================

export function useWebSocket(token, options = {}) {
  const {
    onEvent,
    onConnected,
    onDisconnected,
    onError,
    autoConnect = true,
    channels = [],
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState('disconnected'); // disconnected, connecting, connected, reconnecting
  const [lastEvent, setLastEvent] = useState(null);
  const [metrics, setMetrics] = useState({ eventsReceived: 0, reconnects: 0 });
  
  const wsRef = useRef(null);
  const deduplicatorRef = useRef(new EventDeduplicator());
  const reconnectManagerRef = useRef(new ReconnectManager(WS_CONFIG));
  const heartbeatManagerRef = useRef(null);
  const eventHandlersRef = useRef(new Map());
  const subscriptionsRef = useRef(new Set(channels));
  const lastSequenceRef = useRef(parseInt(localStorage.getItem(WS_CONFIG.lastSequenceKey) || '0'));
  // Marks self-initiated close (e.g. heartbeat timeout) so the close handler
  // can reconnect silently without emitting a "Verbindung unterbrochen"-warning.
  const silentCloseRef = useRef(false);

  // -------------------- SEND MESSAGE --------------------
  
  const send = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  // -------------------- SUBSCRIBE TO CHANNEL --------------------
  
  const subscribe = useCallback((channel) => {
    subscriptionsRef.current.add(channel);
    send({ type: 'subscribe', channel });
    
    // Persist subscriptions
    localStorage.setItem(
      WS_CONFIG.subscriptionsKey,
      JSON.stringify([...subscriptionsRef.current])
    );
  }, [send]);

  // -------------------- UNSUBSCRIBE FROM CHANNEL --------------------
  
  const unsubscribe = useCallback((channel) => {
    subscriptionsRef.current.delete(channel);
    send({ type: 'unsubscribe', channel });
    
    localStorage.setItem(
      WS_CONFIG.subscriptionsKey,
      JSON.stringify([...subscriptionsRef.current])
    );
  }, [send]);

  // -------------------- REGISTER EVENT HANDLER --------------------
  
  const on = useCallback((eventType, handler) => {
    eventHandlersRef.current.set(eventType, handler);
    return () => eventHandlersRef.current.delete(eventType);
  }, []);

  // -------------------- SEND ACK --------------------
  
  const sendAck = useCallback((eventId) => {
    send({
      type: 'ack',
      event_id: eventId,
      timestamp: Date.now()
    });
  }, [send]);

  // -------------------- UPDATE SEQUENCE --------------------
  
  const updateSequence = useCallback((seq) => {
    if (seq > lastSequenceRef.current) {
      lastSequenceRef.current = seq;
      localStorage.setItem(WS_CONFIG.lastSequenceKey, String(seq));
    }
  }, []);

  // -------------------- HANDLE INCOMING MESSAGE --------------------
  
  const handleMessage = useCallback((event) => {
    try {
      const message = JSON.parse(event.data);
      
      switch (message.type) {
        case 'auth_response':
          if (message.status === 'authenticated') {
            setConnectionState('connected');
            setIsConnected(true);
            reconnectManagerRef.current.onConnected();
            heartbeatManagerRef.current?.start();
            
            // Don't auto-subscribe here - let the context handle it
            // This prevents duplicate subscriptions on reconnect
            onConnected?.(message);
          } else {
            onError?.({ type: 'auth_failed', reason: message.reason });
          }
          break;
          
        case 'event':
          const eventData = message.data;
          
          // Deduplicate
          if (deduplicatorRef.current.isDuplicate(eventData.event_id)) {
            console.debug('Duplicate event ignored:', eventData.event_id);
            return;
          }
          
          // Update sequence
          if (eventData.metadata?.sequence) {
            updateSequence(eventData.metadata.sequence);
          }
          
          // Update metrics
          setMetrics(prev => ({ ...prev, eventsReceived: prev.eventsReceived + 1 }));
          setLastEvent(eventData);
          
          // Call specific handler
          const handler = eventHandlersRef.current.get(eventData.event_type);
          if (handler) {
            try {
              handler(eventData);
            } catch (e) {
              // Silent fail — Handler-Exceptions duerfen den WebSocket-Flow
              // nicht stoppen und sollen den User auch nicht mit Toasts spammen.
              // Fix (26.05.2026): vorher toast.error mit nicht-importiertem toast → ReferenceError.
              if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.warn('[WebSocket] handler error:', e?.message || e);
              }
            }
          }
          
          // Call generic handler
          onEvent?.(eventData);
          
          // Send ACK if required
          if (eventData.metadata?.requires_ack) {
            sendAck(eventData.event_id);
          }
          break;
          
        case 'pong':
          heartbeatManagerRef.current?.handlePong();
          break;
          
        case 'subscribed':
          console.debug('Subscribed to channel:', message.channel);
          break;
          
        case 'replay_start':
          break;
          
        case 'replay_end':
          break;
          
        case 'error':
          onError?.(message);
          break;
          
        default:
          console.debug('Unknown message type:', message.type);
      }
    } catch (e) {
      // Non-critical: silently handled
    }
  }, [onConnected, onEvent, onError, sendAck, updateSequence]);

  // -------------------- CONNECT --------------------
  
  // Use ref for connect to avoid circular dependency
  const connectRef = useRef(null);
  
  const connect = useCallback(() => {
    // Check if WebSockets are enabled
    if (!WS_ENABLED) {
      return;
    }
    
    if (!token) {
      return;
    }
    
    // Guard: if a connection is already OPEN or CONNECTING, don't rebuild it.
    // Prevents reconnect-loops when multiple triggers (Visibility-API,
    // ReconnectManager-Timer, effect re-runs) call connect() in quick succession.
    const existing = wsRef.current;
    if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
      return;
    }
    
    // Close existing connection (only if CLOSING/CLOSED).
    // Mark as suppressed so the onclose handler does NOT schedule a reconnect —
    // we're building the new connection right here.
    if (existing) {
      try {
        existing._suppressReconnect = true;
        existing.close(1000, 'Replacing');
      } catch (e) { /* noop */ }
    }
    
    setConnectionState('connecting');
    
    // Build WebSocket URL
    // For custom domains, use the current browser host instead of API_URL
    // This ensures WebSocket connects to the same domain the user is on
    let wsUrl;
    if (API_URL.includes('localhost') || API_URL.includes('127.0.0.1')) {
      // Local development
      wsUrl = API_URL.replace(/^http/, 'ws') + '/ws?token=' + token;
    } else {
      // Production - use current browser host for custom domain support
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = window.location.host; // Use browser's current host (supports custom domains)
      wsUrl = `${wsProtocol}//${wsHost}/api/ws?token=${token}`;
    }
    
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      // Initialize heartbeat manager
      heartbeatManagerRef.current = new HeartbeatManager(
        WS_CONFIG,
        (msg) => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify(msg));
          }
        },
        () => {
          // Self-initiated close: suppress warning toast, reconnect silently
          silentCloseRef.current = true;
          ws.close(4000, 'Heartbeat timeout');
        }
      );
      
      ws.onopen = () => {
        // Auth is handled via query param, wait for auth_response
      };
      
      ws.onmessage = handleMessage;
      
      ws.onclose = (event) => {
        setIsConnected(false);
        heartbeatManagerRef.current?.stop();
        
        // Intentional close while the caller is already building a new
        // connection. Do NOT fire scheduleReconnect -- otherwise we start
        // a reconnect loop (old onclose -> reconnect timer -> kills new
        // connection -> its onclose -> another reconnect -> ...).
        if (ws._suppressReconnect) {
          return;
        }
        
        // Don't reconnect on auth failure
        if (event.code === 4001) {
          setConnectionState('disconnected');
          onError?.({ type: 'auth_failed' });
          return;
        }
        
        setConnectionState('reconnecting');
        setMetrics(prev => ({ ...prev, reconnects: prev.reconnects + 1 }));
        
        // Only notify UI when it was not a self-initiated silent close.
        // Silent close (e.g. heartbeat timeout) still reconnects, but without warning toast.
        if (silentCloseRef.current) {
          silentCloseRef.current = false;
        } else {
          onDisconnected?.({ code: event.code, reason: event.reason });
        }
        
        // Schedule reconnect using ref
        reconnectManagerRef.current.scheduleReconnect(() => {
          if (connectRef.current) {
            connectRef.current();
          }
        });
      };
      
      ws.onerror = (error) => {
      };
      
    } catch (e) {
      setConnectionState('disconnected');
    }
  }, [token, handleMessage, onDisconnected, onError]);
  
  // Update ref when connect changes
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  // -------------------- DISCONNECT --------------------
  
  const disconnect = useCallback(() => {
    reconnectManagerRef.current.cancel();
    heartbeatManagerRef.current?.stop();
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }
    
    setIsConnected(false);
    setConnectionState('disconnected');
  }, []);

  // -------------------- AUTO-CONNECT EFFECT --------------------
  
  // Use refs for connect/disconnect to avoid tearing down the connection
  // on every parent re-render. Without this, the effect fires whenever
  // any callback dep in the chain is rebuilt, causing unnecessary reconnects
  // (and warning toasts).
  const disconnectRef = useRef(null);
  useEffect(() => {
    disconnectRef.current = disconnect;
  }, [disconnect]);
  
  useEffect(() => {
    if (autoConnect && token && WS_ENABLED) {
      // Use timeout to avoid synchronous setState in effect
      const timer = setTimeout(() => {
        connectRef.current?.();
      }, 0);
      return () => {
        clearTimeout(timer);
        disconnectRef.current?.();
      };
    }
    return () => {
      disconnectRef.current?.();
    };
  }, [token, autoConnect]);

  // -------------------- VISIBILITY-API RECONNECT --------------------
  //
  // On mobile (Android PWA, iOS Safari) timers get throttled while the tab
  // is in background. When the tab becomes visible again, the WebSocket may
  // already be closed by the proxy (idle timeout) but we only learn about
  // this on the next ping attempt. Trigger an immediate reconnect check to
  // minimise downtime and avoid the user seeing stale data.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (!WS_ENABLED || !token) return;
      const ws = wsRef.current;
      const isOpen = ws && ws.readyState === WebSocket.OPEN;
      if (!isOpen) {
        connectRef.current?.();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [token]);

  // -------------------- CHANNEL SUBSCRIPTION EFFECT --------------------
  
  useEffect(() => {
    if (isConnected && channels.length > 0) {
      channels.forEach(channel => {
        if (!subscriptionsRef.current.has(channel)) {
          subscribe(channel);
        }
      });
    }
  }, [isConnected, channels, subscribe]);

  return {
    // State
    isConnected,
    connectionState,
    lastEvent,
    metrics,
    
    // Actions
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    send,
    on,
    
    // Utilities
    sendAck,
  };
}


// ==================== CONVENIENCE HOOKS ====================

/**
 * Hook for listening to specific event types
 */
export function useWebSocketEvent(ws, eventType, handler) {
  useEffect(() => {
    if (!ws || !handler) return;
    
    return ws.on(eventType, handler);
  }, [ws, eventType, handler]);
}


/**
 * Hook for job-related WebSocket events
 */
export function useJobEvents(token, options = {}) {
  const {
    onJobCreated,
    onJobStatusChanged,
    onJobAssigned,
    ...wsOptions
  } = options;
  
  const handleEvent = useCallback((event) => {
    switch (event.event_type) {
      case 'job.created':
        onJobCreated?.(event.payload.job, event);
        break;
      case 'job.status_changed':
        onJobStatusChanged?.(event.payload, event);
        break;
      case 'job.assigned':
        onJobAssigned?.(event.payload, event);
        break;
      default:
        break;
    }
  }, [onJobCreated, onJobStatusChanged, onJobAssigned]);
  
  return useWebSocket(token, {
    ...wsOptions,
    onEvent: handleEvent,
  });
}


export default useWebSocket;
